import { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { createAnotherShipmentSuccess } from '../../../../store/orders/order-details/orders.details.actions';
import { createEmptyFulfillmentPlanForSalesOrderIds } from '../../../../store/orders/order-details/orders.details.api';
import { silentRequestGetOrders } from '../../../../store/orders/orders.actions';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../../../modals/order-details-modal/event-tracking/use-track-order-details-action';
import { useEntitiesSalesOrderProperty } from '../../../../hooks/entities/selectors/sales-orders';
import { MarketplaceIds } from '../../../../enums/marketplace-ids';
import Global from '../../../../store/global-selectors';

const useGetMarketplaceName = (isRestrictedByMarketplace, marketplaceId) => {
  return useSelector((state) => {
    if (!isRestrictedByMarketplace) {
      return null;
    }

    const intMarketplaceId = parseInt(marketplaceId, 10);
    const marketplaces = Global.selectReferenceDatagetMarketplaces(state) || emptyArray;
    const { name } = marketplaces.find((m) => m.marketplaceId === intMarketplaceId) || {};
    return name;
  });
};

export const useCreateShipment = (salesOrderId, fulfillmentPlanId) => {
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);

  const { marketplaceId } = useEntitiesSalesOrderProperty(salesOrderId, 'store') || {};
  const isRestrictedByMarketplace = parseInt(marketplaceId, 10) === MarketplaceIds.MercadoLibre;
  const marketplaceName = useGetMarketplaceName(isRestrictedByMarketplace, marketplaceId);

  // The new create another endpoint requires a fulfillmentPlanId, so we want to disable the button if there is no
  // fulfillment plan id (i.e. a return tab is selected)
  const isCreateShipmentDisabled = !fulfillmentPlanId || isRestrictedByMarketplace || isCreatingShipment;

  const dispatch = useDispatch();

  const trackCreateAnotherShipmentClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.CREATE_ANOTHER_SHIPMENT);

  const createShipment = useCallback(async () => {
    setIsCreatingShipment(true);
    try {
      const response = await createEmptyFulfillmentPlanForSalesOrderIds({
        salesOrderId,
        fulfillmentPlanId,
      });
      dispatch(createAnotherShipmentSuccess(salesOrderId, response.fulfillmentPlanId));
      dispatch(silentRequestGetOrders());
      trackCreateAnotherShipmentClick();
    } finally {
      setIsCreatingShipment(false);
    }
  }, [dispatch, salesOrderId, trackCreateAnotherShipmentClick, fulfillmentPlanId]);

  return useMemo(() => {
    return {
      createShipment,
      isCreateShipmentDisabled,
      isRestrictedByMarketplace,
      marketplaceName,
    };
  }, [createShipment, isCreateShipmentDisabled, isRestrictedByMarketplace, marketplaceName]);
};
